import { useState, useEffect, useRef } from "react";

const C = {
  bg:"#0D1117",surface:"#161B22",card:"#1C2230",border:"#2D3748",borderBright:"#3D4F6B",
  text:"#E6EDF3",muted:"#8B949E",dim:"#4A5568",
  blue:"#58A6FF",blueDim:"#1C3A5E",blueGlow:"#1A3A5C",
  green:"#3FB950",greenDim:"#1A3A20",
  amber:"#D29922",amberDim:"#3A2C0A",
  red:"#F85149",redDim:"#3A1A1A",
  purple:"#BC8CFF",purpleDim:"#2D1F4A",
  teal:"#39D2C0",tealDim:"#0D2E2B",
  cyan:"#79C0FF",
};

const s = {
  app:{ background:C.bg, minHeight:"100vh", color:C.text, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:14 },
  sidebar:{ width:240, background:C.surface, borderRight:`1px solid ${C.border}`, height:"calc(100vh - 70px)", position:"fixed", top:70, left:0, overflowY:"auto", zIndex:100 },
  main:{ marginLeft:240, minHeight:"100vh" },
  navItem:(a)=>({ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", cursor:"pointer", borderRadius:8, margin:"2px 8px", background:a?C.blueGlow:"transparent", color:a?C.blue:C.muted, fontWeight:a?600:400, fontSize:13, transition:"all .18s", borderLeft:a?`3px solid ${C.blue}`:"3px solid transparent" }),
  section:{ padding:"32px 40px", maxWidth:1000, margin:"0 auto" },
  h1:{ fontSize:28, fontWeight:700, color:C.text, margin:"0 0 8px", letterSpacing:-0.5 },
  h2:{ fontSize:20, fontWeight:600, color:C.text, margin:"0 0 6px" },
  sub:{ color:C.muted, fontSize:14, lineHeight:1.7, margin:"0 0 24px" },
  card:{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"20px 24px", marginBottom:16 },
  badge:(col)=>({ display:"inline-block", padding:"2px 10px", borderRadius:12, fontSize:11, fontWeight:700, background:col+"22", color:col, border:`1px solid ${col}44`, letterSpacing:0.3 }),
  code:{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"2px 7px", fontFamily:"'Fira Code','Consolas',monospace", fontSize:12, color:C.cyan },
  yamlBlock:{ background:"#0A0E14", border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.blue}`, borderRadius:"0 8px 8px 0", padding:"16px 20px", fontFamily:"'Fira Code','Consolas',monospace", fontSize:12.5, lineHeight:2, overflowX:"auto", margin:"10px 0", whiteSpace:"pre" },
  btn:(col=C.blue,ghost=false)=>({ padding:"8px 18px", borderRadius:8, border:`1px solid ${col}55`, background:ghost?"transparent":col+"22", color:col, cursor:"pointer", fontSize:13, fontWeight:600, transition:"all .18s", display:"inline-flex", alignItems:"center", gap:6 }),
  infoBox:(col)=>({ background:col+"11", border:`1px solid ${col}33`, borderLeft:`3px solid ${col}`, borderRadius:"0 8px 8px 0", padding:"12px 16px", margin:"10px 0", fontSize:13, lineHeight:1.7 }),
  table:{ width:"100%", borderCollapse:"collapse", fontSize:13 },
  th:{ background:C.surface, color:C.muted, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:0.8, padding:"10px 14px", textAlign:"left", borderBottom:`1px solid ${C.border}` },
  td:{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, color:C.text, verticalAlign:"top" },
};

function Yaml({ children }) {
  const lines = children.trim().split("\n");
  return (
    <div style={s.yamlBlock}>
      {lines.map((line, i) => {
        if (line.trimStart().startsWith("#")) return <div key={i}><span style={{color:C.dim}}>{line}</span></div>;
        const colonIdx = line.indexOf(":");
        if (colonIdx > -1 && !line.trimStart().startsWith("-")) {
          const key = line.slice(0, colonIdx+1);
          const val = line.slice(colonIdx+1);
          const indent = key.match(/^(\s*)/)[1];
          const keyPart = key.slice(indent.length);
          return <div key={i}><span style={{color:C.dim}}>{indent}</span><span style={{color:C.blue}}>{keyPart}</span><span style={{color:C.green}}>{val}</span></div>;
        }
        if (line.trimStart().startsWith("- ")) {
          const indent = line.match(/^(\s*)/)[1];
          const rest = line.slice(indent.length+2);
          return <div key={i}><span style={{color:C.dim}}>{indent}</span><span style={{color:C.amber}}>{"- "}</span><span style={{color:C.green}}>{rest}</span></div>;
        }
        return <div key={i}><span style={{color:C.muted}}>{line}</span></div>;
      })}
    </div>
  );
}

function Counter({ target, col=C.blue }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let n=0; const step=Math.ceil(target/50);
    const t=setInterval(()=>{ n+=step; if(n>=target){setVal(target);clearInterval(t);}else setVal(n); },20);
    return ()=>clearInterval(t);
  }, [target]);
  return <span style={{color:col,fontWeight:700}}>{val.toLocaleString()}</span>;
}

function BindingFlow() {
  const [step,setStep]=useState(0);
  const [auto,setAuto]=useState(false);
  const timer=useRef(null);
  const steps=[
    {label:"Dev creates PVC",desc:"Developer writes a PVC manifest requesting 100Gi of block storage. They only care about size and access mode — no knowledge of physical disks needed.",from:"developer",to:"pvc",color:C.purple},
    {label:"Controller scans PVs",desc:"OpenShift's PersistentVolume controller loops every few seconds, comparing PVC requirements (size, access mode, storageClass) against all Available PVs.",from:"pvc",to:"controller",color:C.amber},
    {label:"Match found — binding",desc:"A PV satisfies all requirements. The controller sets PV.status.phase=Bound and PVC.status.phase=Bound, linking them with claimRef. The PV is now exclusively reserved.",from:"controller",to:"pv",color:C.green},
    {label:"Pod mounts the PVC",desc:"The Pod's spec references the PVC by name. Kubelet calls the CSI driver's NodeStageVolume() and NodePublishVolume(), mounting the actual disk into the container's filesystem.",from:"pv",to:"pod",color:C.blue},
    {label:"Data persists!",desc:"Container writes to /var/lib/mysql. If the Pod crashes, rescheduled, or deleted, the PVC stays Bound. The next Pod that claims the same PVC finds the data fully intact.",from:"pod",to:"data",color:C.teal},
  ];
  const nodes=[
    {id:"developer",label:"Developer",icon:"👤",color:C.purple},
    {id:"pvc",label:"PVC",icon:"📋",color:C.amber},
    {id:"controller",label:"PV Controller",icon:"⚙️",color:C.blue},
    {id:"pv",label:"PV",icon:"💾",color:C.green},
    {id:"pod",label:"Pod",icon:"📦",color:C.cyan},
    {id:"data",label:"Data ✓",icon:"🗄️",color:C.teal},
  ];
  useEffect(()=>{
    if(auto){timer.current=setInterval(()=>{setStep(s=>{if(s>=steps.length-1){setAuto(false);return s;}return s+1;});},1600);}
    return()=>clearInterval(timer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[auto]);
  return (
    <div style={s.card}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div style={s.h2}>Storage binding — step by step</div>
        <div style={{display:"flex",gap:8}}>
          <button style={s.btn(C.green)} onClick={()=>{setStep(0);setAuto(true);}}>▶ Auto-play</button>
          <button style={s.btn(C.muted,true)} onClick={()=>{setStep(0);setAuto(false);}}>↺ Reset</button>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"0 0 20px",gap:4}}>
        {nodes.map((n,i)=>{
          const cur=steps[step];
          const isActive=cur&&(cur.from===n.id||cur.to===n.id);
          const isDone=steps.slice(0,step).some(st=>st.to===n.id||st.from===n.id);
          return (
            <div key={n.id} style={{display:"flex",alignItems:"center",flex:1}}>
              <div style={{textAlign:"center",flex:1}}>
                <div style={{width:52,height:52,borderRadius:"50%",border:`2px solid ${isActive?n.color:isDone?n.color+"88":C.border}`,background:isActive?n.color+"22":isDone?n.color+"11":C.surface,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 6px",transition:"all .3s",boxShadow:isActive?`0 0 16px ${n.color}55`:"none"}}>{n.icon}</div>
                <div style={{fontSize:11,color:isActive?n.color:isDone?C.muted:C.dim,fontWeight:isActive?700:400,transition:"all .3s"}}>{n.label}</div>
              </div>
              {i<nodes.length-1&&<div style={{flex:0.3,height:2,background:step>i?steps[Math.min(i,steps.length-1)].color:C.border,transition:"all .5s",borderRadius:2}}/>}
            </div>
          );
        })}
      </div>
      <div style={{background:C.surface,borderRadius:8,padding:"16px 20px",border:`1px solid ${steps[step]?.color||C.border}33`,minHeight:80}}>
        <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
          <div style={s.badge(steps[step]?.color||C.muted)}>Step {step+1}/{steps.length}</div>
          <div>
            <div style={{fontWeight:600,color:steps[step]?.color,marginBottom:6}}>{steps[step]?.label}</div>
            <div style={{color:C.muted,lineHeight:1.7,fontSize:13}}>{steps[step]?.desc}</div>
          </div>
        </div>
      </div>
      <div style={{display:"flex",gap:8,marginTop:12}}>
        <button style={s.btn(C.muted,true)} onClick={()=>setStep(s=>Math.max(0,s-1))} disabled={step===0}>← Prev</button>
        <button style={s.btn(C.blue)} onClick={()=>setStep(s=>Math.min(steps.length-1,s+1))} disabled={step===steps.length-1}>Next →</button>
        <div style={{marginLeft:"auto",display:"flex",gap:6}}>
          {steps.map((_,i)=><div key={i} style={{width:8,height:8,borderRadius:"50%",background:i===step?C.blue:i<step?C.green:C.border,transition:"all .3s"}}/>)}
        </div>
      </div>
    </div>
  );
}

function StorageClassSection() {
  const [sel,setSel]=useState("ebs");
  const classes={
    ebs:{name:"AWS EBS (gp3)",color:C.amber,provisioner:"ebs.csi.aws.com",modes:["RWO"],reclaimDefault:"Delete",bestFor:"Databases, stateful single-replica workloads",whenNotTo:"Multi-pod shared access — use EFS instead",
      yaml:`apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: gp3-rwo
  annotations:
    storageclass.kubernetes.io/is-default-class: "true"
provisioner: ebs.csi.aws.com
parameters:
  type: gp3
  iops: "3000"
  throughput: "125"
  encrypted: "true"
reclaimPolicy: Delete
volumeBindingMode: WaitForFirstConsumer
allowVolumeExpansion: true`,
      details:[
        {label:"WaitForFirstConsumer",desc:"Delays disk creation until a Pod is scheduled — ensures the EBS volume is created in the same AZ as the node. Critical for multi-AZ clusters to avoid cross-AZ mount errors."},
        {label:"allowVolumeExpansion: true",desc:"You can grow the PVC later by editing spec.resources.requests.storage. OpenShift resizes the underlying EBS volume and extends the filesystem online — no downtime needed."},
        {label:"iops: 3000",desc:"gp3 decouples IOPS from size. You can have a 10Gi volume with 3000 IOPS — unlike gp2 where IOPS scaled with size automatically. Saves significant cost at small sizes."},
      ]},
    ceph:{name:"Ceph RBD (ODF)",color:C.purple,provisioner:"openshift-storage.rbd.csi.ceph.com",modes:["RWO","RWX via CephFS"],reclaimDefault:"Delete",bestFor:"On-prem clusters, high-throughput block storage",whenNotTo:"Latency-sensitive workloads needing local NVMe",
      yaml:`apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: ocs-storagecluster-ceph-rbd
provisioner: openshift-storage.rbd.csi.ceph.com
parameters:
  clusterID: <odf-cluster-id>
  pool: ocs-storagecluster-cephblockpool
  imageFormat: "2"
  imageFeatures: layering
  csi.storage.k8s.io/provisioner-secret-name: rook-csi-rbd-provisioner
  csi.storage.k8s.io/controller-expand-secret-name: rook-csi-rbd-provisioner
reclaimPolicy: Delete
allowVolumeExpansion: true
volumeBindingMode: Immediate`,
      details:[
        {label:"ODF (OpenShift Data Foundation)",desc:"ODF is Rook-Ceph packaged and supported by Red Hat. It runs Ceph directly on your OCP nodes, giving you software-defined storage without external SAN hardware."},
        {label:"Ceph RBD vs CephFS",desc:"RBD (RADOS Block Device) = block storage, RWO only, like a virtual disk. CephFS = file storage, RWX capable, POSIX filesystem. Use RBD for databases, CephFS for shared workloads."},
        {label:"imageFeatures: layering",desc:"Enables copy-on-write cloning for snapshots. VolumeSnapshots are fast because they clone only the changed blocks (delta), not the full volume — very space-efficient."},
      ]},
    nfs:{name:"NFS (shared storage)",color:C.teal,provisioner:"nfs.csi.k8s.io",modes:["RWO","RWX","ROX"],reclaimDefault:"Retain",bestFor:"Shared config, build caches, legacy apps needing POSIX shared filesystem",whenNotTo:"Databases — NFS locking issues with MySQL, Postgres write patterns",
      yaml:`apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: nfs-shared
provisioner: nfs.csi.k8s.io
parameters:
  server: nfs-server.ocp.local
  share: /exports/ocp
  onDeletePolicy: delete
reclaimPolicy: Retain
volumeBindingMode: Immediate
allowVolumeExpansion: false
mountOptions:
  - nfsvers=4.1
  - hard
  - timeo=600`,
      details:[
        {label:"RWX capability",desc:"NFS is one of the few backends genuinely supporting ReadWriteMany. All pods across all nodes can mount the same PVC simultaneously — unlike block storage which is node-exclusive."},
        {label:"Database anti-pattern",desc:"Avoid NFS for databases. SQLite, MySQL, and PostgreSQL use POSIX file locks and fsync in ways that NFS doesn't handle reliably, leading to corruption or catastrophic performance degradation."},
        {label:"mountOptions: hard",desc:"Hard mounts retry indefinitely if the NFS server becomes unreachable. Soft mounts return EIO after a timeout. Hard is safer for stateful workloads but can cause hanging pods if NFS goes down."},
      ]},
    local:{name:"Local (node storage)",color:C.red,provisioner:"kubernetes.io/no-provisioner",modes:["RWO"],reclaimDefault:"Retain",bestFor:"Extreme latency-sensitive: Prometheus TSDB, Elasticsearch, Redis AOF",whenNotTo:"Anything requiring pod rescheduling — local storage kills pod mobility",
      yaml:`apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: local-nvme
provisioner: kubernetes.io/no-provisioner
volumeBindingMode: WaitForFirstConsumer
reclaimPolicy: Retain
---
# Manual PV required for local storage
apiVersion: v1
kind: PersistentVolume
metadata:
  name: local-pv-node1-nvme0
spec:
  capacity:
    storage: 500Gi
  accessModes: [ReadWriteOnce]
  persistentVolumeReclaimPolicy: Retain
  storageClassName: local-nvme
  local:
    path: /mnt/nvme0
  nodeAffinity:
    required:
      nodeSelectorTerms:
      - matchExpressions:
        - key: kubernetes.io/hostname
          operator: In
          values: [worker-node-1]`,
      details:[
        {label:"No dynamic provisioning",desc:"kubernetes.io/no-provisioner means the CSI driver can't create volumes on demand. Admins must manually create PVs pointing to local paths or block devices on specific nodes."},
        {label:"nodeAffinity is mandatory",desc:"Local PVs must declare nodeAffinity so the scheduler knows which node the storage exists on. Without it, a pod might be scheduled on a node that doesn't have the disk — and hang forever."},
        {label:"WaitForFirstConsumer critical",desc:"With local storage, binding must wait for pod scheduling so the PV is chosen on the node where the pod lands — not the other way around. Immediate binding would pick randomly and fail."},
      ]},
  };
  const sc=classes[sel];
  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {Object.entries(classes).map(([k,v])=>(
          <button key={k} style={{...s.btn(v.color,sel!==k),fontSize:12}} onClick={()=>setSel(k)}>{v.name}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <div style={s.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div style={{fontWeight:700,color:sc.color,fontSize:16}}>{sc.name}</div>
              <div style={s.badge(sc.color)}>CSI</div>
            </div>
            <table style={{width:"100%",fontSize:13}}>
              <tbody>
              {[["Provisioner",<span style={s.code}>{sc.provisioner}</span>],["Access modes",sc.modes.map(m=><span key={m} style={{...s.badge(m.includes("RWX")?C.green:C.blue),marginRight:4}}>{m}</span>)],["Default reclaim",<span style={s.badge(sc.reclaimDefault==="Delete"?C.red:C.amber)}>{sc.reclaimDefault}</span>],["Best for",sc.bestFor],["Avoid when",sc.whenNotTo]].map(([k,v])=>(
                <tr key={k}><td style={{color:C.muted,padding:"5px 0",verticalAlign:"top",paddingRight:12,whiteSpace:"nowrap"}}>{k}</td><td style={{color:C.text,padding:"5px 0",lineHeight:1.6}}>{v}</td></tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{...s.card,marginTop:0}}>
            <div style={{fontWeight:600,marginBottom:12,color:C.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1}}>Key field explanations</div>
            {sc.details.map((d,i)=>(
              <div key={i} style={{marginBottom:12,paddingBottom:12,borderBottom:i<sc.details.length-1?`1px solid ${C.border}`:"none"}}>
                <div style={{fontFamily:"monospace",fontSize:12,color:sc.color,marginBottom:4}}>{d.label}</div>
                <div style={{color:C.muted,fontSize:13,lineHeight:1.6}}>{d.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:600}}>StorageClass YAML</div>
          <Yaml>{sc.yaml}</Yaml>
        </div>
      </div>
    </div>
  );
}

function LifecycleSimulator() {
  const [phase,setPhase]=useState("none");
  const [reclaim,setReclaim]=useState("Delete");
  const [log,setLog]=useState([]);
  const addLog=(msg,col=C.muted)=>setLog(l=>[...l,{msg,col,t:new Date().toLocaleTimeString()}]);
  const phaseColors={Pending:C.amber,Bound:C.green,Released:C.amber,Available:C.blue,Deleted:C.red,Terminating:C.red};
  const pvcPhase=phase==="created"?"Pending":phase==="bound"?"Bound":phase==="deleted"?"Terminating":phase==="released"?"Released":phase==="reclaimed"?(reclaim==="Retain"?"Released":"Deleted"):"—";
  const pvPhase=phase==="bound"?"Bound":phase==="deleted"?"Bound":phase==="released"?"Released":phase==="reclaimed"?(reclaim==="Retain"?"Available*":"Deleted"):phase==="created"?"Available":"—";
  const actions=[
    {id:"created",label:"1. oc apply -f pvc.yaml",col:C.purple,action:()=>{setPhase("created");addLog("PVC created → status: Pending. Controller searching for matching PV...",C.amber);setTimeout(()=>addLog("No matching PV found yet. PVC stays Pending until a PV satisfies all constraints.",C.muted),800);}},
    {id:"bound",label:"2. Match found (auto-bind)",col:C.green,action:()=>{setPhase("bound");addLog("✅ PV pv-mysql-100g matched! storageClassName + accessMode + size all satisfy PVC.",C.green);addLog("PVC.status.phase = Bound, PV.spec.claimRef = {ns: prod, name: pvc-mysql}",C.muted);}},
    {id:"deleted",label:"3. oc delete pvc pvc-mysql",col:C.amber,action:()=>{setPhase("released");addLog("PVC deletion requested. PVC enters Terminating phase.",C.amber);setTimeout(()=>{addLog(`PVC removed from etcd. PV transitions → Released. Data on disk is untouched.`,C.amber);setPhase("released");},600);}},
    {id:"reclaimed",label:`4. Reclaim (${reclaim} policy)`,col:reclaim==="Delete"?C.red:reclaim==="Retain"?C.teal:C.blue,action:()=>{setPhase("reclaimed");if(reclaim==="Delete")addLog("🗑️ Delete policy: PV deleted. CSI driver called DeleteVolume() — underlying disk destroyed. Storage freed.",C.red);else if(reclaim==="Retain")addLog("🔒 Retain policy: PV stays in Released state. Data preserved. Admin must manually clean and recreate PV to reuse.",C.teal);else addLog("♻️ Recycle policy (legacy): PV scrubbed with rm -rf and returned to Available. Deprecated — use Delete or Retain.",C.amber);}},
  ];
  return (
    <div style={s.card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={s.h2}>PVC/PV lifecycle simulator</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{color:C.muted,fontSize:12}}>Reclaim policy:</span>
          {["Retain","Delete","Recycle"].map(r=><button key={r} style={s.btn(r==="Delete"?C.red:r==="Retain"?C.teal:C.amber,reclaim!==r)} onClick={()=>setReclaim(r)}>{r}</button>)}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        {[["PVC status",pvcPhase],["PV status",pvPhase]].map(([label,ph])=>(
          <div key={label} style={{background:C.surface,borderRadius:8,padding:"12px 16px",border:`1px solid ${phaseColors[ph]||C.border}44`}}>
            <div style={{color:C.muted,fontSize:11,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{label}</div>
            <div style={{fontSize:22,fontWeight:700,color:phaseColors[ph]||C.dim}}>{ph||"—"}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        {actions.map(a=><button key={a.id} style={s.btn(a.col)} onClick={a.action}>{a.label}</button>)}
        <button style={s.btn(C.dim,true)} onClick={()=>{setPhase("none");setLog([]);}}>↺ Reset</button>
      </div>
      <div style={{background:"#080C10",borderRadius:8,padding:"12px 16px",minHeight:100,maxHeight:200,overflowY:"auto",fontFamily:"monospace",fontSize:12}}>
        {log.length===0&&<div style={{color:C.dim}}>Click steps above to simulate lifecycle events...</div>}
        {log.map((entry,i)=>(
          <div key={i} style={{display:"flex",gap:10,marginBottom:4}}>
            <span style={{color:C.dim,flexShrink:0}}>{entry.t}</span>
            <span style={{color:entry.col}}>{entry.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessModeSection() {
  const [sel,setSel]=useState(null);
  const modes=[
    {mode:"ReadWriteOnce",abbr:"RWO",color:C.blue,icon:"🔒",analogy:"Private hotel room — one guest (pod), full access, door locked to others",useCase:"MySQL, PostgreSQL, MongoDB, Redis. Any database that writes exclusively.",wrong:"Cannot run 3 replicas of a Deployment all writing to RWO — only 1 pod mounts at a time. StatefulSets solve this by giving each replica its own PVC.",backends:["AWS EBS","Azure Disk","GCE PD","iSCSI","Ceph RBD"]},
    {mode:"ReadWriteMany",abbr:"RWX",color:C.green,icon:"🏨",analogy:"Hotel library — many guests (pods) on any floor (node) can read and write simultaneously",useCase:"Shared build caches, media serving, GitLab runners, Jupyter notebooks, shared config",wrong:"Do NOT use for databases. Multiple writers without coordination = data corruption. NFS + MySQL = disaster waiting to happen.",backends:["NFS","CephFS (ODF)","Azure Files","Amazon EFS","GlusterFS"]},
    {mode:"ReadOnlyMany",abbr:"ROX",color:C.muted,icon:"📋",analogy:"Hotel bulletin board — many guests can read, only the manager can write (via separate RWO mount)",useCase:"Config datasets, ML model weights, static assets shared across many inference pods",wrong:"Pods using ROX cannot write. Trying to write returns 'Read-only file system' error. Need a separate writer process (CronJob, init container) using RWO.",backends:["NFS","CephFS","Azure Files","S3 via FUSE"]},
    {mode:"ReadWriteOncePod",abbr:"RWOP",color:C.amber,icon:"🔐",analogy:"Safety deposit box — only ONE specific pod can open it, even if multiple pods are on the same node",useCase:"Sensitive single-writer workloads, financial ledger pods, strict write serialization (k8s 1.22+)",wrong:"RWOP is stricter than RWO. Even two pods on the same node — only one can mount. Pod must be terminated before another can claim. Rare but powerful.",backends:["CSI drivers that support RWOP capability"]},
  ];
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        {modes.map(m=>(
          <div key={m.abbr} style={{...s.card,cursor:"pointer",border:`1px solid ${sel===m.abbr?m.color:C.border}`,background:sel===m.abbr?m.color+"11":C.card,transition:"all .2s"}} onClick={()=>setSel(sel===m.abbr?null:m.abbr)}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{fontSize:32}}>{m.icon}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{fontWeight:700,color:m.color}}>{m.mode}</span>
                  <span style={s.badge(m.color)}>{m.abbr}</span>
                </div>
                <div style={{color:C.muted,fontSize:13,lineHeight:1.6,marginBottom:8}}>{m.analogy}</div>
                {sel===m.abbr&&(
                  <div style={{marginTop:12}}>
                    <div style={s.infoBox(C.green)}><strong style={{color:C.green}}>✅ Use for:</strong><br/>{m.useCase}</div>
                    <div style={s.infoBox(C.red)}><strong style={{color:C.red}}>❌ Don't when:</strong><br/>{m.wrong}</div>
                    <div style={{marginTop:10}}>
                      <div style={{color:C.muted,fontSize:11,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Compatible backends</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{m.backends.map(b=><span key={b} style={s.badge(m.color)}>{b}</span>)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={s.card}>
        <div style={{fontWeight:600,marginBottom:12}}>Access mode compatibility matrix</div>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Backend</th><th style={s.th}>RWO</th><th style={s.th}>RWX</th><th style={s.th}>ROX</th><th style={s.th}>RWOP</th></tr></thead>
          <tbody>
            {[["AWS EBS","✅","❌","❌","✅"],["AWS EFS","✅","✅","✅","❌"],["Ceph RBD (ODF)","✅","❌","❌","✅"],["CephFS (ODF)","✅","✅","✅","❌"],["NFS","✅","✅","✅","❌"],["Azure Disk","✅","❌","❌","✅"],["Azure Files","✅","✅","✅","❌"],["Local (hostPath)","✅","❌","❌","❌"]].map(([b,...ms])=>(
              <tr key={b}><td style={{...s.td,fontWeight:500}}>{b}</td>{ms.map((v,i)=><td key={i} style={{...s.td,color:v==="✅"?C.green:C.red,fontSize:16,textAlign:"center"}}>{v}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatefulSetSection() {
  const [replica,setReplica]=useState(0);
  return (
    <div>
      <div style={s.card}>
        <div style={s.h2}>Why StatefulSets, not Deployments, for databases</div>
        <p style={s.sub}>A Deployment with a single RWO PVC works for 1 replica. Scale to 3, and only 1 pod can mount the volume — the other 2 stay Pending with FailedMount events. StatefulSets solve this with volumeClaimTemplates.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div>
            <div style={{...s.infoBox(C.red),marginBottom:12}}>
              <strong style={{color:C.red}}>❌ Wrong: Deployment + shared PVC</strong><br/>
              All replicas try to mount the same RWO PVC. Only 1 wins. The other 2 pods stay in Pending with FailedMount. This is a silent disaster.
            </div>
            <Yaml>{`# ❌ DON'T DO THIS for stateful apps
apiVersion: apps/v1
kind: Deployment
spec:
  replicas: 3       # Only 1 pod will actually work!
  template:
    spec:
      volumes:
      - name: data
        persistentVolumeClaim:
          claimName: pvc-mysql  # All 3 pods share this RWO PVC
      containers:
      - volumeMounts:
        - mountPath: /var/lib/mysql
          name: data`}</Yaml>
          </div>
          <div>
            <div style={{...s.infoBox(C.green),marginBottom:12}}>
              <strong style={{color:C.green}}>✅ Right: StatefulSet + volumeClaimTemplates</strong><br/>
              Each replica gets its own PVC: data-mysql-0, data-mysql-1, data-mysql-2. Pods always rebind to the same PVC on restart.
            </div>
            <Yaml>{`# ✅ Correct: StatefulSet
apiVersion: apps/v1
kind: StatefulSet
metadata:
  name: mysql
spec:
  replicas: 3
  serviceName: mysql
  template:
    spec:
      containers:
      - name: mysql
        volumeMounts:
        - mountPath: /var/lib/mysql
          name: data
  volumeClaimTemplates:      # 🔑 Key field
  - metadata:
      name: data
    spec:
      accessModes: [ReadWriteOnce]
      storageClassName: gp3-rwo
      resources:
        requests:
          storage: 50Gi`}</Yaml>
          </div>
        </div>
      </div>
      <div style={s.card}>
        <div style={{fontWeight:600,marginBottom:16}}>StatefulSet replica → PVC mapping (click a replica)</div>
        <div style={{display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap"}}>
          {[0,1,2].map(i=>(
            <div key={i} style={{cursor:"pointer"}} onClick={()=>setReplica(i)}>
              <div style={{width:90,height:90,borderRadius:12,border:`2px solid ${replica===i?C.blue:C.border}`,background:replica===i?C.blueDim:C.surface,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",transition:"all .2s",boxShadow:replica===i?`0 0 20px ${C.blue}44`:"none"}}>
                <div style={{fontSize:28}}>📦</div>
                <div style={{fontSize:12,color:replica===i?C.blue:C.muted,fontWeight:600}}>mysql-{i}</div>
              </div>
              <div style={{width:2,height:24,background:C.blue+"66",margin:"0 auto"}}/>
              <div style={{background:C.greenDim,border:`1px solid ${C.green}55`,borderRadius:8,padding:"8px 12px",textAlign:"center"}}>
                <div style={{fontSize:18}}>💾</div>
                <div style={{fontSize:11,color:C.green,fontWeight:600}}>data-mysql-{i}</div>
                <div style={{fontSize:10,color:C.muted}}>50Gi · RWO</div>
              </div>
            </div>
          ))}
          <div style={{flex:1,minWidth:220}}>
            <div style={s.infoBox(C.blue)}>
              <strong style={{color:C.blue}}>mysql-{replica} details</strong><br/><br/>
              Pod name: <code style={s.code}>mysql-{replica}</code><br/>
              PVC name: <code style={s.code}>data-mysql-{replica}</code><br/>
              Hostname: <code style={s.code}>mysql-{replica}.mysql.svc.cluster.local</code><br/><br/>
              If this pod is killed, OpenShift always restarts it with ordinal {replica} and rebinds <code style={s.code}>data-mysql-{replica}</code>. Data survives pod restarts, node failures, and rolling upgrades.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SnapshotsSection() {
  const [snapState,setSnapState]=useState("idle");
  const snapLog={idle:[],taking:["Creating VolumeSnapshot object..."],taken:["Creating VolumeSnapshot object...","CSI driver: CreateSnapshot() called on storage backend","Snapshot pv-mysql-100g@snap-20240613 created (copy-on-write)","VolumeSnapshot status: readyToUse: true ✅"],restoring:["Creating PVC from snapshot...","CSI driver: CreateVolume() with DataSource: snapshot","New PVC pvc-mysql-restored provisioned from snapshot","Data restored to point-in-time state ✅"]};
  return (
    <div>
      <div style={s.card}>
        <div style={s.h2}>Volume snapshots — point-in-time backup</div>
        <p style={s.sub}>VolumeSnapshots capture the state of a PVC at a moment in time. Requires a CSI driver that supports snapshots. Copy-on-write means snapshots are fast and space-efficient.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          <div>
            <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:600}}>1. VolumeSnapshotClass (admin setup)</div>
            <Yaml>{`apiVersion: snapshot.storage.k8s.io/v1
kind: VolumeSnapshotClass
metadata:
  name: csi-rbdplugin-snapclass
  annotations:
    snapshot.storage.kubernetes.io/is-default-class: "true"
driver: openshift-storage.rbd.csi.ceph.com
deletionPolicy: Delete`}</Yaml>
          </div>
          <div>
            <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:600}}>2. Take a snapshot of a PVC</div>
            <Yaml>{`apiVersion: snapshot.storage.k8s.io/v1
kind: VolumeSnapshot
metadata:
  name: mysql-snap-20240613
  namespace: prod
spec:
  volumeSnapshotClassName: csi-rbdplugin-snapclass
  source:
    persistentVolumeClaimName: pvc-mysql`}</Yaml>
          </div>
          <div>
            <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:600}}>3. Restore: create PVC from snapshot</div>
            <Yaml>{`apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: pvc-mysql-restored
spec:
  storageClassName: ocs-storagecluster-ceph-rbd
  dataSource:
    name: mysql-snap-20240613
    kind: VolumeSnapshot
    apiGroup: snapshot.storage.k8s.io
  accessModes: [ReadWriteOnce]
  resources:
    requests:
      storage: 100Gi   # Must be >= original`}</Yaml>
          </div>
          <div>
            <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:600}}>4. Simulate snapshot lifecycle</div>
            <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
              <button style={s.btn(C.purple)} onClick={()=>setSnapState("taking")} disabled={snapState!=="idle"}>📸 Take snapshot</button>
              <button style={s.btn(C.green)} onClick={()=>setSnapState("taken")} disabled={snapState!=="taking"}>✓ Complete</button>
              <button style={s.btn(C.blue)} onClick={()=>setSnapState("restoring")} disabled={snapState!=="taken"}>♻️ Restore</button>
              <button style={s.btn(C.dim,true)} onClick={()=>setSnapState("idle")}>↺</button>
            </div>
            <div style={{background:"#080C10",borderRadius:8,padding:"10px 14px",minHeight:100,fontFamily:"monospace",fontSize:12}}>
              {(snapLog[snapState]||[]).map((l,i)=><div key={i} style={{color:i===(snapLog[snapState]||[]).length-1?C.green:C.muted,marginBottom:2}}>{l}</div>)}
              {snapState==="idle"&&<div style={{color:C.dim}}>Click "Take snapshot" to begin...</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpandCard({ title, color, desc, yaml }) {
  const [open,setOpen]=useState(false);
  return (
    <div style={{...s.card,marginBottom:10,borderLeft:`3px solid ${color}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setOpen(!open)}>
        <div style={{fontWeight:600,color}}>{title}</div>
        <span style={{color:C.muted,fontSize:18}}>{open?"−":"+"}</span>
      </div>
      {open&&<div style={{marginTop:12}}><p style={{color:C.muted,fontSize:13,lineHeight:1.7,margin:"0 0 8px"}}>{desc}</p><Yaml>{yaml}</Yaml></div>}
    </div>
  );
}

function MultiClusterSection() {
  const clusters=[
    {name:"prod-us-east-01",env:"prod",pct:78,pvcs:241,orphans:2,sc:"gp3-rwo",status:"healthy"},
    {name:"prod-us-west-02",env:"prod",pct:61,pvcs:198,orphans:0,sc:"gp3-rwo",status:"healthy"},
    {name:"prod-eu-west-01",env:"prod",pct:88,pvcs:312,orphans:5,sc:"ceph-rbd",status:"warn"},
    {name:"prod-ap-south-01",env:"prod",pct:92,pvcs:187,orphans:1,sc:"ceph-rbd",status:"critical"},
    {name:"stage-us-east-01",env:"stage",pct:45,pvcs:89,orphans:7,sc:"gp3-rwo",status:"warn"},
    {name:"dev-shared-01",env:"dev",pct:34,pvcs:55,orphans:12,sc:"nfs-shared",status:"warn"},
    {name:"dr-us-west-01",env:"dr",pct:19,pvcs:32,orphans:0,sc:"gp3-rwo",status:"healthy"},
    {name:"perf-test-01",env:"test",pct:55,pvcs:41,orphans:3,sc:"local-nvme",status:"healthy"},
  ];
  const envCol={prod:C.green,stage:C.amber,dev:C.blue,dr:C.purple,test:C.cyan};
  const statusCol={healthy:C.green,warn:C.amber,critical:C.red};
  const [filter,setFilter]=useState("all");
  const visible=filter==="all"?clusters:filter==="warn"?clusters.filter(c=>c.status!=="healthy"):clusters.filter(c=>c.env===filter);
  const totalPVCs=clusters.reduce((a,c)=>a+c.pvcs,0);
  const totalOrphans=clusters.reduce((a,c)=>a+c.orphans,0);
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[["Total clusters",clusters.length,C.blue],["Total PVCs",totalPVCs,C.green],["Orphaned PVs",totalOrphans,C.amber],["Critical",clusters.filter(c=>c.status==="critical").length,C.red]].map(([l,v,col])=>(
          <div key={l} style={{...s.card,textAlign:"center"}}>
            <div style={{color:C.muted,fontSize:12,marginBottom:4}}>{l}</div>
            <div style={{fontSize:28,fontWeight:700}}><Counter target={v} col={col}/></div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {["all","prod","stage","dev","warn"].map(f=>(
          <button key={f} style={s.btn(f==="warn"?C.amber:C.blue,filter!==f)} onClick={()=>setFilter(f)}>{f==="all"?"All clusters":f==="warn"?"⚠ Issues":`env: ${f}`}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:20}}>
        {visible.map(c=>(
          <div key={c.name} style={{...s.card,borderLeft:`3px solid ${statusCol[c.status]}`,padding:"14px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div style={{fontWeight:600,fontSize:13,color:C.text}}>{c.name}</div>
                <div style={{display:"flex",gap:6,marginTop:4}}>
                  <span style={s.badge(envCol[c.env])}>{c.env}</span>
                  <code style={{...s.code,fontSize:11}}>{c.sc}</code>
                </div>
              </div>
              <span style={s.badge(statusCol[c.status])}>{c.status}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{flex:1,height:5,background:C.border,borderRadius:3,overflow:"hidden"}}>
                <div style={{width:`${c.pct}%`,height:"100%",background:c.pct>85?C.red:c.pct>65?C.amber:C.green,borderRadius:3,transition:"width .6s"}}/>
              </div>
              <span style={{fontSize:12,color:c.pct>85?C.red:c.pct>65?C.amber:C.green,fontWeight:600,width:36}}>{c.pct}%</span>
            </div>
            <div style={{display:"flex",gap:16,fontSize:12,color:C.muted}}>
              <span>{c.pvcs} PVCs</span>
              {c.orphans>0&&<span style={{color:C.amber}}>⚠ {c.orphans} orphaned PVs</span>}
            </div>
          </div>
        ))}
      </div>
      <div style={{fontWeight:700,fontSize:16,color:C.text,marginBottom:12}}>Enterprise storage strategies (200+ clusters)</div>
      {[
        {title:"1 — Dynamic provisioning only",color:C.green,desc:"At scale, nobody manually creates PVs. Set a default StorageClass per cluster. Every PVC without an explicit storageClassName auto-provisions via CSI. Disks appear on demand and vanish with the PVC.",yaml:`# Make a StorageClass the cluster default
metadata:
  annotations:
    storageclass.kubernetes.io/is-default-class: "true"`},
        {title:"2 — GitOps StorageClass templates",color:C.blue,desc:"Store all StorageClass YAMLs in Git. ArgoCD or RHACM applies them to every cluster on sync. No snowflake clusters with local config drift. One PR changes storage policy fleet-wide.",yaml:`# ArgoCD ApplicationSet — push SCs to all clusters
apiVersion: argoproj.io/v1alpha1
kind: ApplicationSet
spec:
  generators:
  - clusterDecisionResource:
      configMapRef: argocd-clusters
  template:
    spec:
      source:
        path: platform/storageclasses/
        targetRevision: main`},
        {title:"3 — RHACM policy enforcement",color:C.purple,desc:"Red Hat Advanced Cluster Management (ACM) lets you write a Policy that enforces a StorageClass exists on every managed cluster. Non-compliant clusters are reported and can be auto-remediated.",yaml:`apiVersion: policy.open-cluster-management.io/v1
kind: Policy
metadata:
  name: enforce-default-storageclass
spec:
  remediationAction: enforce
  policy-templates:
  - objectDefinition:
      apiVersion: policy.open-cluster-management.io/v1
      kind: ConfigurationPolicy
      spec:
        object-templates:
        - complianceType: musthave
          objectDefinition:
            kind: StorageClass
            metadata:
              name: gp3-rwo
              annotations:
                storageclass.kubernetes.io/is-default-class: "true"`},
        {title:"4 — Prometheus alerting for orphaned PVs",color:C.amber,desc:"Released PVs with Retain policy accumulate silently. A simple PromQL alert catches this before it becomes a quota issue across hundreds of clusters.",yaml:`# Alert: Released PVs older than 24h
groups:
- name: storage
  rules:
  - alert: OrphanedPersistentVolume
    expr: kube_persistentvolume_status_phase{phase="Released"} == 1
    for: 24h
    labels:
      severity: warning
    annotations:
      summary: "Released PV {{ $labels.persistentvolume }} not reclaimed"
      runbook: "https://wiki/storage/orphaned-pv-cleanup"`},
        {title:"5 — Namespace storage quotas",color:C.teal,desc:"In multi-tenant clusters, prevent any one team from claiming all storage. ResourceQuota limits total storage requested and number of PVCs per namespace.",yaml:`apiVersion: v1
kind: ResourceQuota
metadata:
  name: storage-quota
  namespace: team-backend
spec:
  hard:
    requests.storage: 500Gi
    persistentvolumeclaims: "20"
    gp3-rwo.storageclass.storage.k8s.io/requests.storage: 200Gi
    nfs-shared.storageclass.storage.k8s.io/requests.storage: 300Gi`},
      ].map((item,i)=><ExpandCard key={i} {...item}/>)}
    </div>
  );
}

function TroubleshootingSection() {
  const [open,setOpen]=useState(null);
  const issues=[
    {title:"PVC stuck in Pending",color:C.amber,icon:"⏳",cause:"No PV satisfies the claim. Common reasons: wrong storageClassName, requesting more storage than available PV, wrong access mode, or no CSI driver installed.",steps:["oc describe pvc <name> — read the Events section at the bottom","Look for: 'no persistent volumes available' or 'no matching PV'","Check PV list: oc get pv — look for Available PVs with matching StorageClass","Check StorageClass: oc get sc — verify the SC name matches exactly (case-sensitive!)","If using dynamic provisioning, check CSI pods in openshift-storage namespace"],cmd:`# Full diagnostic sequence
oc describe pvc pvc-mysql -n prod
oc get pv -o wide
oc get sc
oc get pods -n openshift-storage
# Check CSI driver logs if PV creation fails
oc logs -n openshift-storage -l app=csi-rbdplugin --tail=50`},
    {title:"Pod stuck in ContainerCreating / FailedMount",color:C.red,icon:"🔴",cause:"PVC is Bound but kubelet can't mount the volume to the node. Common causes: wrong node zone (EBS in AZ-a, pod on AZ-b), node taints, or CSI node plugin crash.",steps:["oc describe pod <name> — look for Warning FailedMount events","Check if PV and node are in the same AZ (critical for EBS)","Verify CSI node plugin is running on the target node","Check node events: oc describe node <nodename>","For RWO: ensure no other pod is still mounted (old pod in Terminating state)"],cmd:`# Pod mount failure diagnosis
oc describe pod mysql-0 -n prod
# Look for: FailedMount, VolumeMount timeout

# Check which node the pod is on
oc get pod mysql-0 -o wide

# Check CSI node plugin on that node
oc get pods -n openshift-storage -o wide | grep <node-name>

# Force-delete stuck pod (last resort — data safe if Retain policy)
oc delete pod mysql-0 --grace-period=0 --force`},
    {title:"PVC expansion not working",color:C.purple,icon:"📏",cause:"Tried to increase spec.resources.requests.storage but nothing happened. StorageClass must have allowVolumeExpansion: true, and CSI driver must support online expansion.",steps:["Check the StorageClass: oc get sc <name> -o yaml | grep allowVolumeExpansion","Edit PVC and increase storage size only (you cannot decrease it ever)","Check PVC conditions: oc describe pvc — look for FileSystemResizePending","Pod must be running for online filesystem resize to complete","If conditions show error, check CSI controller pod logs"],cmd:`# Expand a PVC (StorageClass must allow it)
oc patch pvc pvc-mysql -n prod --type='json' \\
  -p='[{"op":"replace","path":"/spec/resources/requests/storage","value":"200Gi"}]'

# Watch the resize happen
oc describe pvc pvc-mysql | grep -A5 Conditions

# Expected: FileSystemResizePending → (pod running) → Bound at new size`},
    {title:"Orphaned Released PVs",color:C.amber,icon:"👻",cause:"PVs with reclaimPolicy: Retain stay in Released state after PVC deletion. Data is safe but the PV can't be reused by a new PVC until admin manually clears claimRef.",steps:["Find all Released PVs: oc get pv --field-selector=status.phase=Released","Inspect each: decide recover data, or clean and reuse?","To reuse: patch the PV to remove claimRef (sets it back to Available)","Then create a new PVC — it will bind to the now-Available PV"],cmd:`# List all orphaned PVs
oc get pv --field-selector=status.phase=Released

# Make a Released PV available again (clean data first!)
oc patch pv pv-mysql-100g --type='json' \\
  -p='[{"op":"remove","path":"/spec/claimRef"}]'

# Verify: STATUS should change Released → Available
oc get pv pv-mysql-100g`},
    {title:"RWX volume causing data corruption",color:C.red,icon:"💥",cause:"Multiple pods writing to an RWX NFS volume hosting a database. NFS doesn't support POSIX advisory locks correctly, causing write conflicts and data corruption.",steps:["Immediately stop all pods writing to the volume","Do NOT use NFS for MySQL, PostgreSQL, SQLite, or MongoDB","Migrate to block storage (RWO) with a StatefulSet per replica","Use NFS only for truly shareable data: logs, media, static config"],cmd:`# Check which pods are mounting the PVC
oc get pods --all-namespaces -o json | \\
  jq '.items[] | select(.spec.volumes[]?.persistentVolumeClaim.claimName=="pvc-nfs") | .metadata.name'

# Check PVC access mode
oc get pvc pvc-nfs -o jsonpath='{.spec.accessModes}'`},
  ];
  return (
    <div>
      <div style={{...s.infoBox(C.blue),marginBottom:20,fontSize:14}}>
        <strong style={{color:C.blue}}>Diagnostic golden rule:</strong> Always start with <code style={s.code}>oc describe pvc &lt;name&gt;</code> and <code style={s.code}>oc describe pod &lt;name&gt;</code>. The Events section contains the actual failure reason 90% of the time.
      </div>
      {issues.map((issue,i)=>(
        <div key={i} style={{...s.card,borderLeft:`3px solid ${issue.color}`,marginBottom:10,cursor:"pointer"}} onClick={()=>setOpen(open===i?null:i)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontSize:20}}>{issue.icon}</span>
              <span style={{fontWeight:600,color:issue.color}}>{issue.title}</span>
            </div>
            <span style={{color:C.muted,fontSize:18}}>{open===i?"−":"+"}</span>
          </div>
          {open===i&&(
            <div style={{marginTop:14}}>
              <div style={s.infoBox(C.amber)}><strong>Root cause:</strong> {issue.cause}</div>
              <div style={{marginTop:12,marginBottom:8,fontWeight:600,color:C.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1}}>Diagnostic steps</div>
              <ol style={{paddingLeft:20,margin:0,color:C.muted,fontSize:13,lineHeight:2}}>
                {issue.steps.map((step,j)=><li key={j}>{step}</li>)}
              </ol>
              <div style={{marginTop:12}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:4,fontWeight:600}}>Commands</div>
                <Yaml>{issue.cmd}</Yaml>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CLISection() {
  const [copied,setCopied]=useState(null);
  const copy=(text,id)=>{navigator.clipboard?.writeText(text);setCopied(id);setTimeout(()=>setCopied(null),2000);};
  const groups=[
    {label:"Inspect storage",color:C.blue,cmds:[
      {desc:"List all StorageClasses",cmd:"oc get sc"},
      {desc:"List all PVs cluster-wide",cmd:"oc get pv --sort-by=.spec.capacity.storage"},
      {desc:"List PVCs in a namespace",cmd:"oc get pvc -n <namespace>"},
      {desc:"Describe PVC (show events)",cmd:"oc describe pvc <name> -n <namespace>"},
      {desc:"Show PVC in YAML",cmd:"oc get pvc <name> -n <namespace> -o yaml"},
      {desc:"Find Released (orphaned) PVs",cmd:"oc get pv --field-selector=status.phase=Released"},
      {desc:"Show which PVC a PV is bound to",cmd:"oc get pv <name> -o jsonpath='{.spec.claimRef.name}'"},
    ]},
    {label:"Create & modify",color:C.green,cmds:[
      {desc:"Apply StorageClass from file",cmd:"oc apply -f storageclass.yaml"},
      {desc:"Create PVC from file",cmd:"oc apply -f pvc.yaml"},
      {desc:"Expand PVC size (SC must allow)",cmd:"oc patch pvc <name> -n <ns> --type='json' -p='[{\"op\":\"replace\",\"path\":\"/spec/resources/requests/storage\",\"value\":\"200Gi\"}]'"},
      {desc:"Change PV reclaim policy",cmd:"oc patch pv <name> -p '{\"spec\":{\"persistentVolumeReclaimPolicy\":\"Retain\"}}'"},
      {desc:"Release PV (remove claimRef)",cmd:"oc patch pv <name> --type='json' -p='[{\"op\":\"remove\",\"path\":\"/spec/claimRef\"}]'"},
    ]},
    {label:"Delete & cleanup",color:C.red,cmds:[
      {desc:"Delete PVC",cmd:"oc delete pvc <name> -n <namespace>"},
      {desc:"Delete all Released PVs (caution!)",cmd:"oc delete pv $(oc get pv --field-selector=status.phase=Released -o name)"},
    ]},
    {label:"Snapshots",color:C.purple,cmds:[
      {desc:"List VolumeSnapshotClasses",cmd:"oc get volumesnapshotclass"},
      {desc:"List snapshots in namespace",cmd:"oc get volumesnapshot -n <namespace>"},
      {desc:"Describe a snapshot",cmd:"oc describe volumesnapshot <name> -n <namespace>"},
    ]},
    {label:"StatefulSets",color:C.teal,cmds:[
      {desc:"List StatefulSets",cmd:"oc get statefulsets -n <namespace>"},
      {desc:"Check StatefulSet PVCs",cmd:"oc get pvc -n <namespace> -l app=mysql"},
      {desc:"Scale StatefulSet",cmd:"oc scale statefulset mysql --replicas=3 -n <namespace>"},
    ]},
  ];
  return (
    <div>
      {groups.map(g=>(
        <div key={g.label} style={{marginBottom:20}}>
          <div style={{fontWeight:700,color:g.color,fontSize:13,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>{g.label}</div>
          {g.cmds.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{flex:"0 0 240px",color:C.muted,fontSize:13}}>{c.desc}</div>
              <code style={{flex:1,fontFamily:"monospace",fontSize:12,color:C.cyan,background:C.surface,padding:"4px 10px",borderRadius:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.cmd}</code>
              <button style={{...s.btn(C.muted,true),padding:"4px 10px",fontSize:11}} onClick={()=>copy(c.cmd,i+g.label)}>{copied===i+g.label?"✓ Copied":"Copy"}</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const NAV=[
  {id:"overview",icon:"🏗️",label:"Overview & Analogy"},
  {id:"binding",icon:"🔗",label:"Binding Flow"},
  {id:"storageclass",icon:"📦",label:"StorageClass Deep Dive"},
  {id:"lifecycle",icon:"🔄",label:"PVC/PV Lifecycle"},
  {id:"access",icon:"🔑",label:"Access Modes"},
  {id:"statefulsets",icon:"🗃️",label:"StatefulSets"},
  {id:"snapshots",icon:"📸",label:"Volume Snapshots"},
  {id:"multicluster",icon:"🌐",label:"200+ Clusters"},
  {id:"troubleshoot",icon:"🔧",label:"Troubleshooting"},
  {id:"cli",icon:"⌨️",label:"CLI Reference"},
];

export default function OpenshiftStorage() {
  const [active,setActive]=useState("overview");
  const sections={
    overview:(
      <div style={s.section}>
        <div style={{marginBottom:32}}>
          <div style={{fontSize:11,color:C.blue,fontWeight:700,textTransform:"uppercase",letterSpacing:2,marginBottom:8}}>OpenShift Storage — Complete Guide</div>
          <h1 style={s.h1}>PV · PVC · StorageClass · CSI</h1>
          <p style={s.sub}>From the hotel analogy to 200+ cluster enterprise management. Click any section in the sidebar to dive deep.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:32}}>
          {[
            {icon:"🏨",name:"PersistentVolume (PV)",color:C.green,analogy:"The actual hotel room",technical:"A cluster-level resource representing a piece of real storage. Created by an admin or auto-provisioned by a CSI driver. Lives outside namespaces.",fields:["capacity.storage","accessModes","persistentVolumeReclaimPolicy","storageClassName","csi.volumeHandle"]},
            {icon:"📋",name:"PersistentVolumeClaim (PVC)",color:C.blue,analogy:"The guest's booking request",technical:"A namespace-level resource where a developer says 'I need 50Gi of block storage'. Pods reference PVCs by name — pods never directly reference PVs.",fields:["spec.accessModes","spec.resources.requests.storage","spec.storageClassName","status.phase","status.capacity"]},
            {icon:"🏷️",name:"StorageClass (SC)",color:C.amber,analogy:"Room category: standard, deluxe, suite",technical:"Defines how storage is provisioned: which CSI driver, parameters, reclaim policy, binding mode. One SC can serve thousands of PVCs automatically.",fields:["provisioner","parameters","reclaimPolicy","volumeBindingMode","allowVolumeExpansion"]},
            {icon:"⚙️",name:"CSI Driver",color:C.purple,analogy:"The hotel's room-allocation system",technical:"Container Storage Interface driver — the plugin that physically creates, attaches, and mounts storage on the underlying infrastructure (AWS, Ceph, NFS, etc.).",fields:["CreateVolume()","DeleteVolume()","NodeStageVolume()","NodePublishVolume()","CreateSnapshot()"]},
          ].map(c=>(
            <div key={c.name} style={{...s.card,borderTop:`3px solid ${c.color}`}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <div style={{fontSize:36}}>{c.icon}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,color:c.color,fontSize:15,marginBottom:2}}>{c.name}</div>
                  <div style={{...s.badge(c.color),marginBottom:10}}>{c.analogy}</div>
                  <p style={{color:C.muted,fontSize:13,lineHeight:1.7,margin:"8px 0"}}>{c.technical}</p>
                  <div style={{marginTop:10}}>
                    <div style={{fontSize:11,color:C.dim,marginBottom:4}}>KEY FIELDS</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{c.fields.map(f=><code key={f} style={{...s.code,fontSize:11}}>{f}</code>)}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{...s.card,borderLeft:`3px solid ${C.amber}`}}>
          <div style={{fontWeight:700,color:C.amber,marginBottom:8}}>⚡ The binding contract — all 3 must match</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,color:C.muted,fontSize:13}}>
            <div><div style={{color:C.text,fontWeight:600,marginBottom:4}}>1. storageClassName</div>Must be identical in PVC and PV. If PVC has no SC, it binds only to PVs with no SC (or the default SC is used for dynamic provisioning).</div>
            <div><div style={{color:C.text,fontWeight:600,marginBottom:4}}>2. accessModes</div>PVC's requested mode must be in the PV's supported list. RWO request can bind to an RWO PV. RWX request cannot bind to an RWO-only PV.</div>
            <div><div style={{color:C.text,fontWeight:600,marginBottom:4}}>3. Storage size</div>PV capacity must be ≥ PVC request. A 100Gi request can bind to a 200Gi PV (wasting 100Gi). It cannot bind to a 50Gi PV under any circumstances.</div>
          </div>
        </div>
      </div>
    ),
    binding:<div style={s.section}><h1 style={s.h1}>Binding flow</h1><p style={s.sub}>Walk through exactly how a PVC finds its PV — step by step, with what OpenShift's controller does at each stage.</p><BindingFlow/></div>,
    storageclass:<div style={s.section}><h1 style={s.h1}>StorageClass deep dive</h1><p style={s.sub}>Each backend has different capabilities, limitations, and configuration. Choose the wrong one and you will see Pending pods forever.</p><StorageClassSection/></div>,
    lifecycle:<div style={s.section}><h1 style={s.h1}>PVC/PV lifecycle</h1><p style={s.sub}>Every PV and PVC has a phase. Understanding what triggers each phase — and what survives deletion — is critical for data safety.</p><LifecycleSimulator/></div>,
    access:<div style={s.section}><h1 style={s.h1}>Access modes</h1><p style={s.sub}>The single most common source of storage bugs: choosing the wrong access mode for the workload. Click each mode to understand the tradeoffs.</p><AccessModeSection/></div>,
    statefulsets:<div style={s.section}><h1 style={s.h1}>StatefulSets and storage</h1><p style={s.sub}>Databases in Kubernetes need StatefulSets, not Deployments. Here is why — and how volumeClaimTemplates solves the per-replica storage problem.</p><StatefulSetSection/></div>,
    snapshots:<div style={s.section}><h1 style={s.h1}>Volume snapshots</h1><p style={s.sub}>Point-in-time backups for PVCs, built into Kubernetes. Uses copy-on-write for efficiency. Requires a CSI driver that supports the snapshot API.</p><SnapshotsSection/></div>,
    multicluster:<div style={s.section}><h1 style={s.h1}>Managing 200+ clusters</h1><p style={s.sub}>Manual PV management does not scale. Here is the enterprise strategy stack: dynamic provisioning, GitOps, RHACM policies, and Prometheus alerting.</p><MultiClusterSection/></div>,
    troubleshoot:<div style={s.section}><h1 style={s.h1}>Troubleshooting</h1><p style={s.sub}>Real storage problems you will encounter, their root causes, diagnostic commands, and resolution steps.</p><TroubleshootingSection/></div>,
    cli:<div style={s.section}><h1 style={s.h1}>CLI reference</h1><p style={s.sub}>Every oc command you need for storage operations, grouped by task. Click Copy to grab any command instantly.</p><CLISection/></div>,
  };
  return (
    <div style={s.app}>
      <div style={s.sidebar}>
        <div style={{padding:"20px 16px 12px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,color:C.blue,fontWeight:700,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>OpenShift</div>
          <div style={{fontSize:15,fontWeight:700,color:C.text}}>Storage Mastery</div>
          <div style={{display:"flex",gap:4,marginTop:8,flexWrap:"wrap"}}>
            {["PV","PVC","SC","CSI"].map(t=><span key={t} style={s.badge(C.blue)}>{t}</span>)}
          </div>
        </div>
        <nav style={{padding:"10px 0"}}>
          {NAV.map(n=>(
            <div key={n.id} style={s.navItem(active===n.id)} onClick={()=>setActive(n.id)}>
              <span style={{fontSize:16}}>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{padding:"12px 16px",borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,color:C.dim,lineHeight:1.6}}>Your cluster:<br/><code style={{color:C.muted,fontFamily:"monospace"}}>lab.ocp.local</code></div>
        </div>
      </div>
      <div style={s.main}>{sections[active]}</div>
    </div>
  );
}
